/**
 * Reads the active PowerPoint presentation as an ArrayBuffer via
 * Office.context.document.getFileAsync (Common API).
 *
 * NOTE: getFileAsync returns the LAST SAVED version of the file.
 * Unsaved changes will not be reflected.
 *
 * Concurrent callers join one in-flight fetch instead of issuing
 * multiple getFileAsync calls — concurrent getFileAsync calls can
 * cause PowerPoint to return "An internal error has occurred."
 */

import { logError, logEvent, scrubMessage } from "../../../../../telemetry";

const SLICE_SIZE = 4 * 1024 * 1024; // 4 MB
const FETCH_TIMEOUT_MS = 15_000;
const TTL_MS = 3_000;
const MAX_RETRIES = 3;

// Linear backoff between retries: attempt 1 waits BASE_BACKOFF_MS, attempt 2
// waits 2×, attempt 3 waits 3× — 400ms, 800ms, 1200ms for the current
// MAX_RETRIES=3 (2400ms worst-case total). That's a small fraction of
// FETCH_TIMEOUT_MS (15s), so the retry budget doesn't need to grow the
// timeout to fit — a getFileAsync call that's simply slow (not
// transient-failing) is exactly what the timeout exists to bound,
// independent of how much of the budget retries themselves use.
const BASE_BACKOFF_MS = 400;

const PPTX_CONNECTOR = "deckorate";

/** Report a warn-level, non-fatal pptxBytes event (a retry attempt, a
 * best-effort close failure) through the shared telemetry pipeline. */
function logPptxWarn(reason: string): void {
  logEvent("connector.action.invoke", "warn", { connectorSystemName: PPTX_CONNECTOR, reason });
}

/** Report a pptxBytes failure through the shared telemetry pipeline
 * (PII-scrubbed). `elapsedMs` is only meaningful for the timeout path. */
function logPptxError(reason: string, type: string, e: unknown, elapsedMs?: number): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(e instanceof Error ? e.message : String(e)) },
    elapsedMs == null
      ? { connectorSystemName: PPTX_CONNECTOR, reason }
      : { connectorSystemName: PPTX_CONNECTOR, reason, elapsedMs },
  );
}

let _cache: ArrayBuffer | null = null;
let _cacheAt = 0;
let _pending: Promise<ArrayBuffer> | null = null;

// Only a SUCCESSFUL fetch is cached (`_cache`/`_cacheAt` are only written in
// the `.then()` below). A failure is never negative-cached: the next call —
// even one made immediately after — reruns the full fetch-with-retry
// sequence from scratch, including its own retry backoff. This is
// intentional, not an oversight: a transient failure shouldn't lock callers
// out of a retry for a cooldown window. The tradeoff is that a *persistently*
// failing host can be re-hammered by every caller that asks for the bytes in
// quick succession — there is no shared backoff/circuit-breaker across
// repeated failures. If that turns out to be a real problem in practice,
// negative-caching would be the fix — but that changes user-visible retry
// behaviour and should be a deliberate decision, not a silent addition here.
export function getActivePresentationBytes(
  forceRefresh = false,
  signal?: AbortSignal,
): Promise<ArrayBuffer> {
  if (signal?.aborted) {
    return Promise.reject(new DOMException("getActivePresentationBytes aborted before starting", "AbortError"));
  }

  const now = Date.now();
  if (!forceRefresh && _cache && now - _cacheAt < TTL_MS) {
    return Promise.resolve(_cache);
  }

  _pending ??= withTimeout(_fetchWithRetry(), FETCH_TIMEOUT_MS, "getActivePresentationBytes")
    .then((buf) => {
      _cache = buf;
      _cacheAt = Date.now();
      return buf;
    })
    .finally(() => {
      _pending = null;
    });

  // Race THIS caller's view of the shared in-flight fetch against their own
  // signal. `_pending` itself is never reassigned or cleared here, so an
  // abort only makes THIS call's returned promise settle early — the
  // underlying fetch keeps running for any other caller still awaiting the
  // same `_pending`, and still populates the cache on success.
  return abortable(_pending, signal);
}

function abortable<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(new DOMException("getActivePresentationBytes aborted", "AbortError"));
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (val) => {
        signal.removeEventListener("abort", onAbort);
        resolve(val);
      },
      (err) => {
        signal.removeEventListener("abort", onAbort);
        reject(err);
      },
    );
  });
}

export function clearPptxCache(): void {
  _cache = null;
  _cacheAt = 0;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Marks a rejection that came from getFileAsync's callback reporting a
// Failed status (as opposed to Office being unavailable, or getFileAsync
// throwing synchronously) — only this kind is eligible for retry/wrapping.
class FileAsyncFailure extends Error {}

// Thrown once retries are exhausted (or the failure was non-transient from
// the first attempt) and wrapped into a user-actionable message. A dedicated
// class (rather than a bare TypeError, which normally signals a wrong-type
// argument, not an external API failure) so callers can `instanceof`-check
// this specifically instead of catching every TypeError in the app.
export class PresentationReadError extends Error {}

// Thrown when the overall fetch-with-retry sequence exceeds
// FETCH_TIMEOUT_MS. Distinct from PresentationReadError so logs/callers can
// tell "gave up because retries were exhausted" apart from "gave up because
// nothing responded in time at all" (which may still have retries in
// flight — see the backoff-budget note on BASE_BACKOFF_MS above).
export class PptxTimeoutError extends Error {}

// A single, non-retried getFileAsync call, promisified.
function getFileOnce(): Promise<Office.File> {
  return new Promise((resolve, reject) => {
    const office = (globalThis as { Office?: typeof Office }).Office;
    if (!office?.context?.document) {
      const err = new Error("Office context not available");
      logPptxError("pptx-office-unavailable", "OfficeUnavailableError", err);
      reject(err);
      return;
    }
    try {
      office.context.document.getFileAsync(
        office.FileType.Compressed,
        { sliceSize: SLICE_SIZE },
        (result) => {
          if (result.status === office.AsyncResultStatus.Failed) {
            reject(new FileAsyncFailure(result.error?.message ?? "unknown"));
            return;
          }
          resolve(result.value);
        },
      );
    } catch (err) {
      const wrapped = new Error("getFileAsync threw: " + (err instanceof Error ? err.message : String(err)));
      logPptxError("pptx-getfileasync-threw", "GetFileAsyncThrewError", wrapped);
      reject(wrapped);
    }
  });
}

function isTransient(err: unknown): boolean {
  return err instanceof FileAsyncFailure && err.message.toLowerCase().includes("internal");
}

// Retries getFileOnce on a transient ("internal error") Failed status,
// with linear backoff. Office-unavailable and synchronous-throw failures
// are never retried and propagate as-is; an exhausted/non-transient Failed
// status is wrapped with a user-actionable message.
async function getFileWithRetry(): Promise<Office.File> {
  for (let attemptsLeft = MAX_RETRIES; ; attemptsLeft--) {
    try {
      return await getFileOnce(); // NOSONAR retry attempts must run one after another
    } catch (err) {
      const attemptNumber = MAX_RETRIES - attemptsLeft + 1;
      if (attemptsLeft > 0 && isTransient(err)) {
        // Attempt number is bounded to MAX_RETRIES distinct values (3
        // today), so folding it into `reason` doesn't blow up telemetry
        // cardinality the way an unbounded value (e.g. a shape id) would —
        // there's no separate "attempt number" field in the shared
        // telemetry schema's closed property-key list.
        logPptxWarn(`pptx-fetch-retry-attempt-${attemptNumber}-of-${MAX_RETRIES}`);
        await sleep(attemptNumber * BASE_BACKOFF_MS); // NOSONAR retry attempts must run one after another
        continue;
      }
      if (err instanceof FileAsyncFailure) {
        logPptxError(`pptx-fetch-retry-exhausted-after-${attemptNumber}-attempts`, "FileAsyncFailure", err);
        throw new PresentationReadError(
          `Could not read presentation: ${err.message}. Try saving (Ctrl+S) and retrying.`,
        );
      }
      throw err;
    }
  }
}

async function _fetchWithRetry(): Promise<ArrayBuffer> {
  const file = await getFileWithRetry();
  return drainSlices(file);
}

// A single slice read, promisified. `totalCount` is only used to make a
// failure's error message self-describing ("which slice, out of how many").
function getSliceOnce(file: Office.File, idx: number, totalCount: number): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    file.getSliceAsync(idx, (sr) => {
      if (sr.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(`Slice read failed at ${idx + 1}/${totalCount}: ${sr.error?.message ?? "?"}`));
        return;
      }
      const raw = sr.value.data as number[] | ArrayBuffer;
      resolve(Array.isArray(raw) ? new Uint8Array(raw) : new Uint8Array(raw as ArrayBuffer));
    });
  });
}

// Closes the file handle. Kept non-throwing — a close failure shouldn't
// fail the overall read — but a failure is now logged (warn) rather than
// silently ignored via an unchecked callback param.
function closeFile(file: Office.File): Promise<void> {
  return new Promise((resolve) => {
    file.closeAsync((result) => {
      if (result?.status === Office.AsyncResultStatus.Failed) {
        logPptxWarn("pptx-close-failed");
      }
      resolve();
    });
  });
}

function concatSlices(slices: Uint8Array[]): ArrayBuffer {
  const total = slices.reduce((s, sl) => s + sl.byteLength, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const slice of slices) {
    out.set(slice, offset);
    offset += slice.byteLength;
  }
  return out.buffer;
}

async function drainSlices(file: Office.File): Promise<ArrayBuffer> {
  const count = file.sliceCount;
  if (count === 0) {
    await closeFile(file);
    const err = new Error("Presentation appears empty — save (Ctrl+S) and retry.");
    logPptxError("pptx-empty-presentation", "EmptyPresentationError", err);
    throw err;
  }
  try {
    const slices: Uint8Array[] = [];
    for (let idx = 0; idx < count; idx++) {
      try {
        slices.push(await getSliceOnce(file, idx, count)); // NOSONAR file slices are read in order
      } catch (err) {
        logPptxError("pptx-slice-read-failed", "SliceReadError", err);
        throw err;
      }
    }
    return concatSlices(slices);
  } finally {
    await closeFile(file);
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      const err = new PptxTimeoutError(`${label} timed out after ${ms / 1000}s — save the file (Ctrl+S) and retry.`);
      logPptxError("pptx-fetch-timeout", "PptxTimeoutError", err, ms);
      reject(err);
    }, ms);
    promise.then(
      (val) => { if (!settled) { settled = true; clearTimeout(timer); resolve(val); } },
      (err) => { if (!settled) { settled = true; clearTimeout(timer); reject(err); } },
    );
  });
}
